import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Upload, Trash2, Loader2, Lock, Mic, Plus, X } from "lucide-react";
import { toast } from "sonner";

export default function Voices() {
    const { user, loading } = useAuth();
    const nav = useNavigate();
    const [myVoices, setMyVoices] = useState([]);
    const [open, setOpen] = useState(false);
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    const isPro = user?.subscription_status === "pro";

    useEffect(() => {
        if (!loading && !user) nav("/auth?next=/voices");
    }, [loading, user, nav]);

    const refresh = async () => {
        try {
            const r = await api.get("/voices/mine");
            setMyVoices(r.data.voices || []);
        } catch { /* */ }
    };

    useEffect(() => {
        if (user) refresh();
    }, [user]);

    const submit = async (e) => {
        e.preventDefault();
        if (!isPro) { nav("/pricing"); return; }
        if (!name.trim() || !file) return;
        setBusy(true);
        const fd = new FormData();
        fd.append("name", name.trim());
        if (description) fd.append("description", description);
        fd.append("file", file);
        try {
            await api.post("/voices/clone", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Voice cloned");
            setOpen(false); setName(""); setDescription(""); setFile(null);
            refresh();
        } catch (err) {
            toast.error("Cloning failed", { description: err?.response?.data?.detail || err.message });
        } finally {
            setBusy(false);
        }
    };

    const remove = async (voice_id) => {
        if (!window.confirm("Delete this cloned voice?")) return;
        try {
            await api.delete(`/voices/${voice_id}`);
            setMyVoices((p) => p.filter((v) => v.voice_id !== voice_id));
            toast.success("Voice deleted");
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    if (loading || !user) return null;

    return (
        <div className="min-h-screen bg-white">
            <Header />
            <main className="max-w-3xl mx-auto px-6 py-12 sm:py-16">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
                    <div>
                        <p className="text-xs font-medium text-slate-400 uppercase tracking-widest">AI Studio</p>
                        <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900" data-testid="voices-title">
                            Your voices.
                        </h1>
                        <p className="mt-2 text-slate-500">Clone a voice from a sample and use it anywhere with TTS.</p>
                    </div>
                    <button
                        onClick={() => isPro ? setOpen(true) : nav("/pricing")}
                        className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 text-white px-4 py-2.5 text-sm font-medium hover:bg-indigo-700 transition-colors"
                        data-testid="clone-voice-btn"
                    >
                        {isPro ? <Plus className="w-4 h-4" strokeWidth={2.25} /> : <Lock className="w-3.5 h-3.5" strokeWidth={2} />}
                        {isPro ? "Clone a voice" : "Upgrade to clone"}
                    </button>
                </div>

                {!isPro && (
                    <div className="mb-8 rounded-2xl border border-amber-200 bg-amber-50/60 p-5 text-sm text-amber-900" data-testid="voices-upgrade-banner">
                        Voice cloning is a Pro-only feature. Upgrade from <button onClick={() => nav("/pricing")} className="font-medium underline">$5/mo</button>.
                    </div>
                )}

                {myVoices.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-200 py-20 text-center" data-testid="voices-empty">
                        <div className="w-12 h-12 mx-auto rounded-xl bg-slate-50 flex items-center justify-center">
                            <Mic className="w-6 h-6 text-slate-400" strokeWidth={1.5} />
                        </div>
                        <p className="mt-4 text-sm text-slate-500">No cloned voices yet.</p>
                        <p className="text-xs text-slate-400 mt-1">Upload a 30s–3min clean voice sample to clone.</p>
                    </div>
                ) : (
                    <ul className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="voices-list">
                        {myVoices.map((v) => (
                            <li key={v.voice_id} className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50 group">
                                <div className="w-9 h-9 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                                    <Mic className="w-4 h-4 text-indigo-600" strokeWidth={1.75} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-slate-900 truncate">{v.name}</p>
                                    <p className="text-xs text-slate-400 truncate">{v.description || v.voice_id}</p>
                                </div>
                                <button
                                    onClick={() => remove(v.voice_id)}
                                    className="opacity-0 group-hover:opacity-100 p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                    data-testid={`delete-voice-${v.voice_id}`}
                                >
                                    <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}

                {/* Clone dialog */}
                {open && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4" data-testid="clone-modal">
                        <form onSubmit={submit} className="w-full max-w-md rounded-2xl bg-white border border-slate-200 shadow-xl overflow-hidden fade-in-up">
                            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
                                <h3 className="text-base font-semibold text-slate-900">Clone a voice</h3>
                                <button type="button" onClick={() => setOpen(false)} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md" data-testid="clone-modal-close">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="p-6 space-y-4">
                                <div>
                                    <label className="text-xs font-medium text-slate-500">Voice name</label>
                                    <input
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                        maxLength={80}
                                        placeholder="My narrator voice"
                                        className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500"
                                        data-testid="clone-name-input"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-slate-500">Description (optional)</label>
                                    <input
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        maxLength={500}
                                        placeholder="Warm, mid-range, calm"
                                        className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500"
                                        data-testid="clone-desc-input"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-slate-500">Sample audio (30s–3min)</label>
                                    <input
                                        ref={fileRef}
                                        type="file"
                                        accept="audio/*,.mp3,.wav,.m4a,.flac"
                                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                                        className="hidden"
                                        data-testid="clone-file-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => fileRef.current?.click()}
                                        className="mt-1 w-full inline-flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 bg-white px-4 py-6 text-sm text-slate-600 hover:border-indigo-300 hover:bg-indigo-50/30 transition-colors"
                                    >
                                        <Upload className="w-4 h-4" strokeWidth={1.75} />
                                        {file ? file.name : "Click to upload sample"}
                                    </button>
                                    <p className="mt-1.5 text-xs text-slate-400">Clean, single-speaker, no music. ≤ 25 MB.</p>
                                </div>
                            </div>
                            <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
                                <button type="button" onClick={() => setOpen(false)} className="inline-flex items-center justify-center rounded-xl bg-white px-4 py-2 text-sm font-medium text-slate-700 border border-slate-200 hover:bg-slate-50">
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={busy || !name.trim() || !file}
                                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 text-white px-4 py-2 text-sm font-medium hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                    data-testid="clone-submit-btn"
                                >
                                    {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                                    Clone voice
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </main>
        </div>
    );
}
